/**
 * `/audit-log` — WP-K8, Task 48. `web/shapes/audit-log.md` is the agreed shape.
 *
 * ── The acceptance criterion decides the design ────────────────────────────
 *
 * *"Role auditor read-only thao tác được toàn bộ."* An auditor shown greyed buttons
 * has been told they are the wrong kind of user; an auditor shown only what they may
 * do has been given a tool. So every control an auditor cannot use is **absent**, and
 * on this page that is nearly all of them, because there are nearly none:
 *
 *  - **No write control exists, for anybody.** Both tables are append-only and
 *    `API_CONTRACT.md` §8.10 says the read-only contract "is not an oversight". The
 *    immutability note says so in words, because an auditor needs to know the record
 *    is immutable and a greyed bin icon says the opposite.
 *  - **The export link is the one permission-gated control**, and it is gated on
 *    `workforce.export_reports`, which a read-only auditor does not hold. Absent, not
 *    disabled — Task 45's revoke button, again.
 *  - The access gate's own submit **is** disabled while the reason is too short, and
 *    that is the single honest greying on this build: it says "you have not finished
 *    saying what you are doing", a true statement about the operator rather than
 *    about permission. `AccessReasonGate`'s docblock settles it.
 *
 * ── Reading the audit log is itself a sensitive access ─────────────────────
 *
 * §8.10 in as many words, and `openapi.yaml` gives the operation
 * `x-wp-sensitive-access: true` and the `AccessReason` parameter. So the page opens
 * behind the shared gate and **issues nothing** until a usable reason exists — the
 * hook's `path` is `null`, its own "there is nothing to ask yet". A screen that fired
 * a read it knew would be refused would teach people that refusals are noise.
 *
 * The second reason is particular to this screen: the auditor reading other people's
 * reasons will appear in this very table with their own reason beside their name.
 * Collecting it behind a form that says where it goes is the only honest way to do it.
 *
 * ── `X-Access-Reason` cannot carry Vietnamese (G20) ────────────────────────
 *
 * `Headers.set` throws on `đ`, so a Vietnamese reason means **the request is never
 * sent**. The shared gate fails closed — refused in the field, said out loud, never
 * transliterated and never percent-encoded, because a reason the operator did not
 * write is a fabricated audit record and worse than a refused one. The fix is a server
 * decision and is proposed in `web/shapes/audit-log.md` §9.3, not implemented here.
 *
 * ── The endpoint is not mounted, and that sentence must be exactly right ───
 *
 * `GET /api/v1/audit-logs` is fully specified and has no route in `src/`. See
 * `web/src/audit/availability.ts` for why a `404` on this **collection** path is
 * unambiguous, and why saying "no audit events" instead would be the worst thing this
 * screen could do.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { AccessReasonGate } from '../access/AccessReasonGate';
import { accessHeaders } from '../access/reason';
import {
  AUDIT_PAGE_LIMIT,
  EMPTY_FILTERS,
  auditPath,
  isNarrowed,
  withCursor,
  withFilters,
  type AuditFilters,
} from '../audit/filters';
import type { AuditLogListResponse } from '../api/types';
import { useApiQuery } from '../api/useApiQuery';
import { useSession } from '../auth/SessionProvider';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { EmptyState } from '../states/EmptyState';
import { PartialData } from '../states/PartialData';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import { ScreenHeading } from '../shell/ScreenHeading';
import { AuditFilterBar } from './audit/AuditFilterBar';
import { AuditRow } from './audit/AuditRow';

export function AuditLogScreen(): ReactNode {
  const { t } = useI18n();
  const { user } = useSession();
  const [reason, setReason] = useState('');
  // The draft is what the fields edit; `applied` is what the request is built from.
  // Separate, because this read writes a `wp_data_access_logs` row and a filter that
  // re-fired on every keystroke would bury the rows somebody is looking for under the
  // auditor's own typing.
  const [draft, setDraft] = useState<AuditFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<AuditFilters>(EMPTY_FILTERS);

  // `useApiQuery` depends on the header object by **serialisation**, not identity,
  // precisely so a caller may build it inline without re-issuing a sensitive read —
  // and therefore writing a `wp_data_access_logs` row — on every render. Built the
  // same way `/alerts`, `/incidents` and `/data-risk` build theirs.
  const headers = accessHeaders(reason);
  const headerOption = headers === undefined ? {} : { headers };

  const path = headers === undefined ? null : auditPath(applied, AUDIT_PAGE_LIMIT);
  const read = useApiQuery<AuditLogListResponse>(path, 0, headerOption);

  const rows = read.data?.data ?? [];
  const page = read.data?.page ?? null;
  const zone = user?.timezone ?? 'UTC';
  const canExport = (user?.permissions ?? []).includes('workforce.export_reports');

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('nav.auditLog')}</ScreenHeading>
      <p className="wp-small wp-muted" data-testid="k8-audit-intro">
        {t('k8.audit.intro')}
      </p>

      <AccessReasonGate
        value={reason}
        subject={t('k8.audit.subjectWord')}
        testId="k8-audit-access"
        onOpen={setReason}
      />

      {reason === '' ? (
        <StatusNote severity="info" title={t('access.closed.title')} testId="k8-audit-closed">
          <p>{t('access.closed.body', { subject: t('k8.audit.subjectWord') })}</p>
        </StatusNote>
      ) : (
        <>
          <p className="wp-small wp-muted" data-testid="k8-audit-self-logged">
            {t('k8.audit.selfLogged')}
          </p>

          <AuditFilterBar
            draft={draft}
            onChange={(patch) => {
              setDraft((current) => withFilters(current, patch));
            }}
            onApply={() => {
              // The cursor is dropped by `withFilters` on every field change, so
              // applying always starts a fresh walk. A cursor carried across a
              // changed query would ask the server to continue a walk that no longer
              // exists — in an audit log, of all places.
              setApplied(draft);
            }}
            onClear={() => {
              setDraft(EMPTY_FILTERS);
              setApplied(EMPTY_FILTERS);
            }}
          />

          <section
            className="wp-section"
            aria-labelledby="k8-audit-results-heading"
            data-testid="k8-audit-results"
          >
            <h2 className="wp-section__title" id="k8-audit-results-heading">
              {t('k8.audit.results.title')}
            </h2>

            {read.status === 'loading' ? (
              <Skeleton
                rows={4}
                variant="block"
                label={t('k8.audit.subjectWord')}
                testId="k8-audit-skeleton"
              />
            ) : null}

            {read.status === 'error' && read.error !== null ? (
              <ApiErrorNote
                error={read.error}
                onRetry={read.reload}
                subject={t('k8.audit.subjectWord')}
                testId="k8-audit-error"
              />
            ) : null}

            {read.data === null ? null : (
              <>
                <p data-testid="k8-audit-count">
                  {t('k8.audit.results.count', { count: rows.length })}
                </p>
                <p className="wp-small wp-muted" data-testid="k8-audit-no-total">
                  {t('k8.audit.results.noTotal')}
                </p>

                {page?.has_more === true ? (
                  <PartialData
                    excludes={t('k8.audit.results.count', { count: rows.length })}
                    testId="k8-audit-partial"
                  />
                ) : null}

                {rows.length === 0 ? (
                  <EmptyState
                    title={
                      isNarrowed(applied)
                        ? t('k8.audit.empty.narrowed.title')
                        : t('k8.audit.empty.wide.title')
                    }
                    description={
                      isNarrowed(applied)
                        ? t('k8.audit.empty.narrowed.body')
                        : t('k8.audit.empty.wide.body')
                    }
                    testId="k8-audit-empty"
                  />
                ) : (
                  <div className="wp-table-scroll">
                    <table className="wp-table wp-table--wide" data-testid="k8-audit-table">
                      <thead>
                        <tr>
                          <th scope="col">{t('k8.audit.col.actor')}</th>
                          {/* The action column carries `log_type` under the verb, so the
                              head names both facts — the second one only to a screen
                              reader, because the type's own words already say what it is. */}
                          <th scope="col">
                            {t('k8.audit.col.action')}
                            <span className="wp-visually-hidden">
                              {' · '}
                              {t('k8.audit.col.type')}
                            </span>
                          </th>
                          <th scope="col">{t('k8.audit.col.resource')}</th>
                          <th scope="col">{t('k8.audit.col.when')}</th>
                          <th scope="col">{t('k8.audit.col.result')}</th>
                          <th scope="col">{t('k8.audit.col.reason')}</th>
                          <th scope="col">{t('k8.audit.detail.title')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((entry) => (
                          <AuditRow key={entry.id} entry={entry} timezone={zone} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Present only when there is somewhere to go. A greyed "next" at the
                    end of a stream says "you may not go on"; the truth is that there
                    is nothing further, which is a different sentence. */}
                {page?.has_more === true && typeof page.next_cursor === 'string' ? (
                  <button
                    className="wp-button"
                    type="button"
                    data-testid="k8-audit-next"
                    onClick={() => {
                      const cursor = page.next_cursor;
                      if (cursor === null) return;
                      setApplied((current) => withCursor(current, cursor));
                    }}
                  >
                    {t('k8.audit.results.next')}
                  </button>
                ) : null}
              </>
            )}
          </section>

          <StatusNote
            severity="info"
            title={t('k8.audit.immutable.title')}
            testId="k8-audit-immutable"
          >
            <p>{t('k8.audit.immutable.body')}</p>
          </StatusNote>

          {/* The one permission-gated control on this page, and the acceptance
              criterion's teeth: a read-only auditor does not hold
              `workforce.export_reports`, so this is **not in the DOM** for them.
              Absent, never disabled — Task 45's revoke button. */}
          {canExport ? (
            <p data-testid="k8-audit-export">
              <a className="wp-linklike" href="/reports">
                {t('k8.audit.export.link')}
              </a>{' '}
              <span className="wp-small wp-muted">{t('k8.audit.export.hint')}</span>
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
